import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  Stack,
  Step,
  StepLabel,
  Stepper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  ArrowDropDown as ArrowDropDownIcon,
  ArrowForward as ArrowForwardIcon,
  BarChart as BarChartIcon,
  Download as DownloadIcon,
  ExpandMore as ExpandMoreIcon,
  KeyboardArrowDown as KeyboardArrowDownIcon,
  KeyboardArrowUp as KeyboardArrowUpIcon,
  PlayArrow as PlayArrowIcon,
} from '@mui/icons-material';
import { arrayMove } from '@dnd-kit/sortable';
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  Cell,
  Line,
  LineChart,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import * as XLSX from 'xlsx';
import { useSnackbar } from 'notistack';
import SearchableSelect from '../SearchableSelect';
import ClienteInlineSearchField from '../Tesoreria/ClienteInlineSearchField';
import ClienteSearch from '../Tesoreria/ClienteSearch';
import ProveedorInlineSearchField from '../ProveedorInlineSearchField';
import ProveedorSearch from '../ProveedorSearch';
import ProductoSearch from '../ProductoSearch';
import bancoService, { Banco } from '../../services/bancoService';
import comprasInformesService from '../../services/comprasInformesService';
import contabilidadInformesService from '../../services/contabilidadInformesService';
import tesoreriaInformesService from '../../services/tesoreriaInformesService';
import ventasInformesService from '../../services/ventasInformesService';
import informesPersonalizadosService from '../../services/informesPersonalizadosService';
import sucursalesVentaService, { SucursalVenta } from '../../services/sucursalesVentaService';
import tipoFormularioService, { TIPO_FORMULARIO_MAX_PAGE_SIZE, TipoFormulario } from '../../services/tipoFormularioService';
import { getApiErrorMessage } from '../../utils/apiError';
import {
  AccesoInformePersonalizado,
  BorradorInformePersonalizado,
  ErrorValidacionInforme,
  InformePersonalizadoDetalle,
  UsuarioEmpresaOpcion,
  armarPayloadInforme,
  estadoDesdeDetalle,
  leerErroresValidacion,
  mensajeDeClave,
  statusHttp,
} from '../../utils/informePersonalizadoDefinicion';
import {
  InformeAggregation,
  InformeField,
  InformeFilter,
  summarizeInformeRows,
} from './informeDinamicoUtils';
import {
  cuentasConsultaContabilidad,
  cuentasImpNroCuentaParaConsulta,
  filtrarFilasInformeVentas,
  migrarFiltrosAdicionalesVentas,
} from './filtrosAdicionalesVentas';
import FiltrosAdicionalesVentas from './FiltrosAdicionalesVentasPanel';

type ModuloInforme = 'Ventas' | 'Compras' | 'Contabilidad' | 'Tesoreria';
type TipoInforme = 'detalle' | 'resumen';
type TipoGrafico = 'barras' | 'lineas' | 'torta' | 'area' | 'ninguno';

interface InformeDinamicoWizardProps {
  modulo: ModuloInforme;
  onBack: () => void;
  definicion?: InformePersonalizadoDetalle | null;
  acceso?: AccesoInformePersonalizado;
  usuariosEmpresa?: UsuarioEmpresaOpcion[];
  usuariosEmpresaError?: string;
  onSaved?: (detalle: InformePersonalizadoDetalle) => void;
}

interface FiltrosOrigen {
  fechaDesde: string;
  fechaHasta: string;
  entidadId: string;
  codigoArticulo: string;
  tipoFormulario: string;
  sucursal: string;
  nroCuenta: string;
  nroBanco: string;
  maxRows: string;
}

const STEPS = ['Tipo de informe', 'Origen', 'Configuración', 'Compartir', 'Vista previa'];
const PASO_COMPARTIR = 3;
const PASO_VISTA_PREVIA = 4;
const filtroFieldSx = { '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: '#fff' } };
const accionButtonSx = { textTransform: 'none', borderRadius: 2, fontWeight: 500 };
const CHART_COLORS = ['#1976d2', '#42a5f5', '#26a69a', '#ffa726', '#ab47bc', '#ef5350'];
const AGGREGATIONS: { value: InformeAggregation; label: string }[] = [
  { value: 'sum', label: 'Suma' },
  { value: 'average', label: 'Promedio' },
  { value: 'count', label: 'Cantidad de renglones' },
  { value: 'min', label: 'Mínimo' },
  { value: 'max', label: 'Máximo' },
];

function getInitialFilters(): FiltrosOrigen {
  const now = new Date();
  const start = new Date(now.getFullYear(), now.getMonth(), 1);
  const toInputDate = (date: Date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };
  return {
    fechaDesde: toInputDate(start),
    fechaHasta: toInputDate(now),
    entidadId: '',
    codigoArticulo: '',
    tipoFormulario: '',
    sucursal: '',
    nroCuenta: '',
    nroBanco: '',
    maxRows: '5000',
  };
}

function esModuloVista(modulo: ModuloInforme): boolean {
  return modulo === 'Contabilidad' || modulo === 'Tesoreria';
}

function read(row: object, key: string): unknown {
  return Reflect.get(row, key);
}

function saldoMovimiento(row: object): number {
  const debe = Number(read(row, 'debe'));
  const haber = Number(read(row, 'haber'));
  return (Number.isFinite(debe) ? debe : 0) - (Number.isFinite(haber) ? haber : 0);
}

function firstValue(row: object, keys: string[]): unknown {
  for (const key of keys) {
    const value = read(row, key);
    if (value != null && value !== '') return value;
  }
  return null;
}

function nombreModulo(modulo: ModuloInforme): string {
  if (modulo === 'Compras') return 'compras';
  if (modulo === 'Contabilidad') return 'contabilidad';
  if (modulo === 'Tesoreria') return 'tesorería';
  return 'ventas';
}

function tituloOrigen(modulo: ModuloInforme): string {
  if (modulo === 'Compras') return 'Detalle de compras';
  if (modulo === 'Contabilidad') return 'Movimientos contables';
  if (modulo === 'Tesoreria') return 'Detalle de carteras';
  return 'Detalle de ventas';
}

function detalleOrigen(modulo: ModuloInforme): string {
  if (modulo === 'Contabilidad') {
    return 'Vista BP_RPT_Contabilidad_vw: asientos, cuentas y comprobantes.';
  }
  if (modulo === 'Tesoreria') {
    return 'BP_RPT_CarterasDet_vw: movimientos de carteras y cuentas, con debe y haber.';
  }
  return 'Comprobantes y artículos — origen disponible para este módulo.';
}

function columnasInicialesDe(modulo: ModuloInforme): string[] {
  if (modulo === 'Contabilidad') return COLUMNAS_INICIALES_CONTABILIDAD;
  if (modulo === 'Tesoreria') return COLUMNAS_INICIALES_TESORERIA;
  return COLUMNAS_INICIALES_VENTAS;
}

function metricasDe(modulo: ModuloInforme): string[] {
  if (modulo === 'Contabilidad') return METRICAS_CONTABILIDAD;
  if (modulo === 'Tesoreria') return METRICAS_TESORERIA;
  return METRICAS_VENTAS;
}

function agrupacionDefault(modulo: ModuloInforme): string {
  if (modulo === 'Contabilidad') return 'NombreCuenta';
  if (modulo === 'Tesoreria') return 'NombreBanco';
  return 'entidad';
}

function medidaDefault(modulo: ModuloInforme): string {
  if (modulo === 'Contabilidad' || modulo === 'Tesoreria') return 'Debe';
  return 'subtotal';
}

const COLUMNAS_INICIALES_VENTAS = ['fecha', 'comprobante', 'entidad', 'articulo', 'cantidad', 'subtotal'];
const COLUMNAS_INICIALES_CONTABILIDAD = ['FechaAsiento', 'NroAsiento', 'NombreCuenta', 'Detalle', 'Debe', 'Haber'];
const COLUMNAS_INICIALES_TESORERIA = ['FechaEmision', 'Descripcion', 'NroComprobante', 'NombreBanco', 'Debe', 'Haber'];
const METRICAS_VENTAS = ['cantidad', 'precioUnitario', 'subtotal', 'total'];
const METRICAS_CONTABILIDAD = ['Debe', 'Haber', 'Saldo', 'ImporteTotal', 'ImporteSinIva', 'Subtotal'];
const METRICAS_TESORERIA = ['Debe', 'Haber'];

function createFieldsContabilidad(): InformeField[] {
  return [
    { id: 'FechaAsiento', label: 'Fecha de asiento', type: 'date', getValue: (row) => read(row, 'fechaAsiento') },
    { id: 'NroAsiento', label: 'Número de asiento', type: 'number', getValue: (row) => read(row, 'nroAsiento') },
    { id: 'ImpNroCuenta', label: 'Número de cuenta', type: 'number', getValue: (row) => read(row, 'impNroCuenta') },
    { id: 'NombreCuenta', label: 'Cuenta', type: 'text', getValue: (row) => read(row, 'nombreCuenta') },
    { id: 'NomCuenta', label: 'Rubro', type: 'text', getValue: (row) => read(row, 'nomCuenta') },
    { id: 'NroRubro', label: 'Número de rubro', type: 'number', getValue: (row) => read(row, 'nroRubro') },
    { id: 'TipoCuentaImputa', label: 'Tipo de cuenta', type: 'text', getValue: (row) => read(row, 'tipoCuentaImputa') },
    { id: 'DescripcionCta', label: 'Descripción de cuenta', type: 'text', getValue: (row) => read(row, 'descripcionCta') },
    { id: 'Detalle', label: 'Detalle', type: 'text', getValue: (row) => read(row, 'detalle') },
    { id: 'LidiAclaracion', label: 'Aclaración del asiento', type: 'text', getValue: (row) => read(row, 'lidiAclaracion') },
    { id: 'Debe', label: 'Debe', type: 'number', getValue: (row) => read(row, 'debe') },
    { id: 'Haber', label: 'Haber', type: 'number', getValue: (row) => read(row, 'haber') },
    { id: 'Saldo', label: 'Saldo', type: 'number', getValue: (row) => saldoMovimiento(row) },
    { id: 'Contabilizado', label: 'Contabilizado', type: 'text', getValue: (row) => read(row, 'contabilizado') },
    { id: 'Asentado', label: 'Asentado', type: 'text', getValue: (row) => read(row, 'asentado') },
    { id: 'NroTipoFormulario', label: 'Tipo de comprobante', type: 'text', getValue: (row) => read(row, 'nroTipoFormulario') },
    { id: 'Letra', label: 'Letra', type: 'text', getValue: (row) => read(row, 'letra') },
    { id: 'NroSuc', label: 'Sucursal', type: 'number', getValue: (row) => read(row, 'nroSuc') },
    { id: 'NroComprobante', label: 'Comprobante', type: 'number', getValue: (row) => read(row, 'nroComprobante') },
    { id: 'NroFormulario', label: 'Formulario', type: 'number', getValue: (row) => read(row, 'nroFormulario') },
    { id: 'FechaEmision', label: 'Fecha de emisión', type: 'date', getValue: (row) => read(row, 'fechaEmision') },
    { id: 'NroCliente', label: 'Número de cliente', type: 'number', getValue: (row) => read(row, 'nroCliente') },
    { id: 'Nombre', label: 'Cliente', type: 'text', getValue: (row) => read(row, 'nombre') },
    { id: 'Cuit', label: 'CUIT', type: 'text', getValue: (row) => read(row, 'cuit') },
    { id: 'Subtotal', label: 'Subtotal', type: 'number', getValue: (row) => read(row, 'subtotal') },
    { id: 'ImporteSinIva', label: 'Importe sin IVA', type: 'number', getValue: (row) => read(row, 'importeSinIva') },
    { id: 'ImporteTotal', label: 'Importe total', type: 'number', getValue: (row) => read(row, 'importeTotal') },
    { id: 'FormaDePago', label: 'Forma de pago', type: 'text', getValue: (row) => read(row, 'formaDePago') },
    { id: 'Usuario', label: 'Usuario', type: 'text', getValue: (row) => read(row, 'usuario') },
    { id: 'VisibleEn', label: 'Visible en', type: 'text', getValue: (row) => read(row, 'visibleEn') },
    { id: 'Descripcion', label: 'Descripción', type: 'text', getValue: (row) => read(row, 'descripcion') },
  ];
}

function createFieldsTesoreria(): InformeField[] {
  return [
    { id: 'FechaEmision', label: 'Fecha de emisión', type: 'date', getValue: (row) => read(row, 'fechaEmision') },
    { id: 'NroFormulario', label: 'Formulario', type: 'number', getValue: (row) => read(row, 'nroFormulario') },
    { id: 'Descripcion', label: 'Tipo de comprobante', type: 'text', getValue: (row) => read(row, 'descripcion') },
    { id: 'Letra', label: 'Letra', type: 'text', getValue: (row) => read(row, 'letra') },
    { id: 'NroSuc', label: 'Sucursal', type: 'number', getValue: (row) => read(row, 'nroSuc') },
    { id: 'NroComprobante', label: 'Comprobante', type: 'number', getValue: (row) => read(row, 'nroComprobante') },
    { id: 'NroBanco', label: 'Número de cartera', type: 'number', getValue: (row) => read(row, 'nroBanco') },
    { id: 'NombreBanco', label: 'Cartera', type: 'text', getValue: (row) => read(row, 'nombreBanco') },
    { id: 'TipoDeCuenta', label: 'Tipo de cuenta', type: 'text', getValue: (row) => read(row, 'tipoDeCuenta') },
    { id: 'NroDeCuenta', label: 'Número de cuenta', type: 'text', getValue: (row) => read(row, 'nroDeCuenta') },
    { id: 'Debe', label: 'Debe', type: 'number', getValue: (row) => read(row, 'debe') },
    { id: 'Haber', label: 'Haber', type: 'number', getValue: (row) => read(row, 'haber') },
    { id: 'NroTipoPago', label: 'Tipo de pago', type: 'number', getValue: (row) => read(row, 'nroTipoPago') },
  ];
}

function createFields(modulo: ModuloInforme): InformeField[] {
  if (modulo === 'Contabilidad') return createFieldsContabilidad();
  if (modulo === 'Tesoreria') return createFieldsTesoreria();
  const entidad = modulo === 'Ventas' ? 'Cliente' : 'Proveedor';
  const entidadKey = modulo === 'Ventas' ? 'cliNombre' : 'proNombre';
  return [
    { id: 'fecha', label: 'Fecha', type: 'date', getValue: (row) => firstValue(row, ['fecha', 'fechaEmision']) },
    { id: 'comprobante', label: 'Comprobante', type: 'text', getValue: (row) => firstValue(row, ['nroComprobante', 'nroFormulario']) },
    { id: 'tipo', label: 'Tipo de comprobante', type: 'text', getValue: (row) => firstValue(row, ['tipDescripcion', 'nroTipoFormulario']) },
    { id: 'entidad', label: entidad, type: 'text', getValue: (row) => firstValue(row, [entidadKey, 'nombre']) },
    { id: 'entidadNumero', label: `Número de ${entidad.toLowerCase()}`, type: 'number', getValue: (row) => read(row, 'nroCliente') },
    { id: 'articuloCodigo', label: 'Código de artículo', type: 'text', getValue: (row) => firstValue(row, ['iteCodigoArticulo', 'codigoArticulo']) },
    { id: 'articulo', label: 'Artículo', type: 'text', getValue: (row) => firstValue(row, ['iteDescripcion', 'descripcion']) },
    { id: 'cantidad', label: 'Cantidad de artículos (unidades)', type: 'number', getValue: (row) => read(row, 'iteCantidad') },
    { id: 'precioUnitario', label: 'Precio unitario', type: 'number', getValue: (row) => firstValue(row, ['precioUnitarioFinal', 'precioUnitario']) },
    { id: 'subtotal', label: 'Subtotal', type: 'number', getValue: (row) => firstValue(row, ['subtotalFinal', 'subtotal']) },
    { id: 'total', label: 'Importe total', type: 'number', getValue: (row) => read(row, 'importeTotal') },
    { id: 'sucursal', label: 'Sucursal', type: 'number', getValue: (row) => read(row, 'nroSuc') },
    { id: 'vendedor', label: 'Vendedor', type: 'text', getValue: (row) => read(row, 'venNombreVendedor') },
    { id: 'formaPago', label: 'Forma de pago', type: 'text', getValue: (row) => read(row, 'formaDePago') },
    { id: 'ubicacion', label: 'Ubicación', type: 'number', getValue: (row) => read(row, 'nroUbicacion') },
    { id: 'ciudad', label: 'Ciudad', type: 'text', getValue: (row) => read(row, 'ciuNombreCiudad') },
    { id: 'provincia', label: 'Provincia', type: 'text', getValue: (row) => read(row, 'pciaNombreProvincia') },
    { id: 'ramo', label: 'Ramo', type: 'text', getValue: (row) => read(row, 'nombreRamo') },
    { id: 'linea', label: 'Línea', type: 'text', getValue: (row) => read(row, 'nombreLinea') },
    { id: 'clase', label: 'Clase', type: 'text', getValue: (row) => read(row, 'nombreClase') },
    { id: 'usuario', label: 'Usuario', type: 'text', getValue: (row) => read(row, 'usuario') },
  ];
}

function formatValue(value: unknown): string {
  if (value == null || value === '') return '—';
  if (typeof value === 'number') {
    return new Intl.NumberFormat('es-AR', { maximumFractionDigits: 2 }).format(value);
  }
  return String(value);
}

function safeFileName(value: string): string {
  const normalized = value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');
  return normalized || 'informe-personalizado';
}

const InformeDinamicoWizard: React.FC<InformeDinamicoWizardProps> = ({
  modulo,
  onBack,
  definicion = null,
  acceso = 'propietario',
  usuariosEmpresa = [],
  usuariosEmpresaError = '',
  onSaved,
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const catalogoInicial = createFields(modulo);
  const hydrated = definicion
    ? estadoDesdeDetalle(
      definicion,
      catalogoInicial.map((field) => field.id),
      catalogoInicial.filter((field) => field.type === 'date').map((field) => field.id),
    )
    : null;
  const [activeStep, setActiveStep] = useState(0);
  const [tipoInforme, setTipoInforme] = useState<TipoInforme>(hydrated?.tipo ?? 'detalle');
  const [titulo, setTitulo] = useState(hydrated?.titulo ?? '');
  const [filtrosOrigen, setFiltrosOrigen] = useState<FiltrosOrigen>(() => {
    const initial = getInitialFilters();
    if (!hydrated) return initial;
    return {
      ...initial,
      fechaDesde: hydrated.fechaDesde || initial.fechaDesde,
      fechaHasta: hydrated.fechaHasta || initial.fechaHasta,
      entidadId: hydrated.entidadId,
      codigoArticulo: hydrated.codigoArticulo,
      tipoFormulario: hydrated.nroTipoFormulario,
      sucursal: hydrated.nroSuc,
      nroCuenta: hydrated.nroCuenta ?? '',
      nroBanco: hydrated.nroBanco ?? '',
    };
  });
  const columnasIniciales = columnasInicialesDe(modulo);
  const [columnasSeleccionadas, setColumnasSeleccionadas] = useState<string[]>(
    hydrated?.columnas ?? columnasIniciales,
  );
  const [ordenColumnas, setOrdenColumnas] = useState<string[]>(() => {
    const base = createFields(modulo).map((field) => field.id);
    if (!hydrated?.columnas.length) return base;
    const selected = hydrated.columnas.filter((fieldId) => base.includes(fieldId));
    return [...selected, ...base.filter((fieldId) => !selected.includes(fieldId))];
  });
  const [columnasDisponiblesMarcadas, setColumnasDisponiblesMarcadas] = useState<string[]>([]);
  const [columnasSeleccionadasMarcadas, setColumnasSeleccionadasMarcadas] = useState<string[]>([]);
  const [filtrosAdicionales, setFiltrosAdicionales] = useState<InformeFilter[]>(() => {
    const iniciales = (hydrated?.filtros ?? []).map((filtro, index) => ({ ...filtro, id: index + 1 }));
    if (modulo === 'Contabilidad' || modulo === 'Tesoreria') return iniciales;
    return migrarFiltrosAdicionalesVentas(iniciales, modulo);
  });
  const [campoAgrupacion, setCampoAgrupacion] = useState(
    hydrated?.campoAgrupacion ?? agrupacionDefault(modulo),
  );
  const [campoValor, setCampoValor] = useState(
    hydrated?.medida ?? medidaDefault(modulo),
  );
  const [agregacion, setAgregacion] = useState<InformeAggregation>(hydrated?.agregacion ?? 'sum');
  const [tipoGrafico, setTipoGrafico] = useState<TipoGrafico>(hydrated?.tipoGrafico ?? 'ninguno');
  const [usuariosVer, setUsuariosVer] = useState<number[]>(hydrated?.usuariosVer ?? []);
  const [usuariosEditar, setUsuariosEditar] = useState<number[]>(hydrated?.usuariosEditar ?? []);
  const [savedId, setSavedId] = useState<number | null>(definicion?.id ?? null);
  const [accesoActual, setAccesoActual] = useState<AccesoInformePersonalizado>(definicion ? acceso : 'propietario');
  const [guardando, setGuardando] = useState(false);
  const [erroresValidacion, setErroresValidacion] = useState<ErrorValidacionInforme[]>([]);
  const [rawRows, setRawRows] = useState<object[]>([]);
  const [totalMatched, setTotalMatched] = useState(0);
  const [truncated, setTruncated] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [entidadNombre, setEntidadNombre] = useState('');
  const [articuloNombre, setArticuloNombre] = useState('');
  const [entidadSearchOpen, setEntidadSearchOpen] = useState(false);
  const [productoSearchOpen, setProductoSearchOpen] = useState(false);
  const [sucursales, setSucursales] = useState<SucursalVenta[]>([]);
  const [loadingSucursales, setLoadingSucursales] = useState(false);
  const [tiposFormulario, setTiposFormulario] = useState<TipoFormulario[]>([]);
  const [loadingTipos, setLoadingTipos] = useState(false);
  const [bancos, setBancos] = useState<Banco[]>([]);
  const [loadingBancos, setLoadingBancos] = useState(false);
  const filterIdRef = useRef((hydrated?.filtros.length ?? 0) + 1);

  useEffect(() => {
    let cancelled = false;
    setLoadingSucursales(true);
    setLoadingTipos(true);

    void (async () => {
      try {
        const result = await sucursalesVentaService.getSucursalesVenta({ pageNumber: 1, pageSize: 500 });
        if (!cancelled) setSucursales(result.items ?? []);
      } catch {
        if (!cancelled) setSucursales([]);
      } finally {
        if (!cancelled) setLoadingSucursales(false);
      }
    })();

    void (async () => {
      try {
        const items: TipoFormulario[] = [];
        let pageNumber = 1;
        let totalPages = 1;
        do {
          const result = await tipoFormularioService.getTipoFormularios({
            pageNumber,
            pageSize: TIPO_FORMULARIO_MAX_PAGE_SIZE,
          });
          items.push(...(result.items ?? []));
          totalPages = result.totalPages || 1;
          pageNumber += 1;
        } while (pageNumber <= totalPages && pageNumber <= 50);
        if (!cancelled) setTiposFormulario(items);
      } catch {
        if (!cancelled) setTiposFormulario([]);
      } finally {
        if (!cancelled) setLoadingTipos(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (modulo !== 'Tesoreria') return undefined;
    let cancelled = false;
    setLoadingBancos(true);
    void (async () => {
      try {
        const items: Banco[] = [];
        let pageNumber = 1;
        let totalPages = 1;
        do {
          const result = await bancoService.getBancos({ pageNumber, pageSize: 20 });
          items.push(...(result.items ?? []));
          totalPages = result.totalPages || 1;
          pageNumber += 1;
        } while (pageNumber <= totalPages && pageNumber <= 50);
        if (!cancelled) {
          items.sort((left, right) => left.nroBanco - right.nroBanco);
          setBancos(items);
        }
      } catch {
        if (!cancelled) setBancos([]);
      } finally {
        if (!cancelled) setLoadingBancos(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [modulo]);

  const entidadDisplay = filtrosOrigen.entidadId.trim()
    ? [filtrosOrigen.entidadId.trim(), entidadNombre.trim()].filter(Boolean).join(' - ')
    : '';
  const fields = useMemo(() => createFields(modulo), [modulo]);
  const selectedFields = useMemo(
    () => ordenColumnas
      .filter((fieldId) => columnasSeleccionadas.includes(fieldId))
      .map((fieldId) => fields.find((field) => field.id === fieldId))
      .filter((field): field is InformeField => Boolean(field)),
    [columnasSeleccionadas, fields, ordenColumnas],
  );
  const numericFields = useMemo(
    () => fields.filter((field) => metricasDe(modulo).includes(field.id)),
    [fields, modulo],
  );
  const groupField = fields.find((field) => field.id === campoAgrupacion) ?? fields[0];
  const metricField = fields.find((field) => field.id === campoValor) ?? numericFields[0];
  const filteredRows = useMemo(
    () => filtrarFilasInformeVentas(rawRows, filtrosAdicionales, fields),
    [fields, filtrosAdicionales, modulo, rawRows],
  );
  const summaryRows = useMemo(
    () => (groupField && metricField
      ? summarizeInformeRows(filteredRows, groupField, metricField, agregacion)
      : []),
    [agregacion, filteredRows, groupField, metricField],
  );
  const isSummary = tipoInforme === 'resumen';
  const readOnly = accesoActual === 'consulta';
  const canShare = accesoActual === 'propietario';
  const canSave = !readOnly;
  const errorDe = (clave: string) => mensajeDeClave(erroresValidacion, clave);
  const opcionesUsuarios = useMemo(() => {
    const conocidos = new Map(usuariosEmpresa.map((usuario) => [usuario.id, usuario]));
    [...usuariosVer, ...usuariosEditar].forEach((id) => {
      if (!conocidos.has(id)) conocidos.set(id, { id, nombre: `Usuario ${id}`, usuario: '' });
    });
    return Array.from(conocidos.values());
  }, [usuariosEditar, usuariosEmpresa, usuariosVer]);
  const summaryValueLabel = agregacion === 'count'
    ? 'Cantidad de renglones'
    : `${AGGREGATIONS.find((item) => item.value === agregacion)?.label ?? 'Resumen'} de ${metricField?.label ?? 'registros'}`;

  const setOriginField = (field: keyof FiltrosOrigen, value: string) => {
    setFiltrosOrigen((previous) => ({ ...previous, [field]: value }));
  };

  const toggleMarkedColumn = (
    fieldId: string,
    markedColumns: string[],
    setMarkedColumns: React.Dispatch<React.SetStateAction<string[]>>,
  ) => {
    setMarkedColumns((previous) => previous.includes(fieldId)
      ? previous.filter((id) => id !== fieldId)
      : [...previous, fieldId]);
  };

  const addSelectedColumns = () => {
    const newlySelected = availableFields
      .filter((field) => columnasDisponiblesMarcadas.includes(field.id))
      .map((field) => field.id);
    setColumnasSeleccionadas((previous) => [...previous, ...newlySelected]);
    setOrdenColumnas((previous) => {
      const selectedOrder = [
        ...previous.filter((fieldId) => columnasSeleccionadas.includes(fieldId)),
        ...newlySelected,
      ];
      return [
        ...selectedOrder,
        ...previous.filter((fieldId) => !selectedOrder.includes(fieldId)),
      ];
    });
    setColumnasDisponiblesMarcadas([]);
  };

  const removeSelectedColumns = () => {
    setColumnasSeleccionadas((previous) =>
      previous.filter((fieldId) => !columnasSeleccionadasMarcadas.includes(fieldId)),
    );
    setColumnasSeleccionadasMarcadas([]);
  };

  const moveSelectedColumn = (fieldId: string, direction: -1 | 1) => {
    setOrdenColumnas((previous) => {
      const selectedOrder = previous.filter((id) => columnasSeleccionadas.includes(id));
      const index = selectedOrder.indexOf(fieldId);
      const nextIndex = index + direction;
      if (index < 0 || nextIndex < 0 || nextIndex >= selectedOrder.length) return previous;
      const reorderedSelected = arrayMove(selectedOrder, index, nextIndex);
      let selectedIndex = 0;
      return previous.map((id) => (
        columnasSeleccionadas.includes(id) ? reorderedSelected[selectedIndex++] : id
      ));
    });
  };

  const orderedFields = ordenColumnas
    .map((fieldId) => fields.find((field) => field.id === fieldId))
    .filter((field): field is InformeField => Boolean(field));
  const availableFields = orderedFields.filter((field) => !columnasSeleccionadas.includes(field.id));
  const selectedColumnFields = orderedFields.filter((field) => columnasSeleccionadas.includes(field.id));

  const loadPreview = async () => {
    if (!filtrosOrigen.fechaDesde || !filtrosOrigen.fechaHasta) {
      setError('Completá las fechas desde y hasta.');
      return;
    }
    if (filtrosOrigen.fechaDesde > filtrosOrigen.fechaHasta) {
      setError('La fecha desde no puede ser posterior a la fecha hasta.');
      return;
    }
    const maxRows = Number(filtrosOrigen.maxRows);
    if (!Number.isInteger(maxRows) || maxRows < 1 || maxRows > 15000) {
      setError('El máximo de filas debe ser un número entero entre 1 y 15.000.');
      return;
    }
    const entityId = filtrosOrigen.entidadId.trim() ? Number(filtrosOrigen.entidadId) : undefined;
    const branch = filtrosOrigen.sucursal.trim() ? Number(filtrosOrigen.sucursal) : undefined;
    const nroCuenta = filtrosOrigen.nroCuenta.trim() ? Number(filtrosOrigen.nroCuenta) : undefined;
    const nroBanco = filtrosOrigen.nroBanco.trim() ? Number(filtrosOrigen.nroBanco) : undefined;
    if ((entityId !== undefined && (!Number.isInteger(entityId) || entityId < 1))
      || (branch !== undefined && (!Number.isInteger(branch) || branch < 1))
      || (nroCuenta !== undefined && (!Number.isInteger(nroCuenta) || nroCuenta < 1))
      || (nroBanco !== undefined && (!Number.isInteger(nroBanco) || nroBanco < 1))) {
      setError('El cliente, el proveedor, la cuenta, la cartera y la sucursal deben ser números enteros positivos.');
      return;
    }
    if (isSummary && !campoAgrupacion) {
      setError('Elegí un campo para agrupar el resumen.');
      return;
    }
    if (tipoInforme === 'detalle' && selectedFields.length === 0) {
      setError('Seleccioná al menos una columna para la tabla.');
      return;
    }

    const cuentasConsulta = modulo === 'Contabilidad'
      ? cuentasConsultaContabilidad(nroCuenta, cuentasImpNroCuentaParaConsulta(filtrosAdicionales))
      : undefined;
    if (cuentasConsulta && cuentasConsulta.length === 0) {
      setRawRows([]);
      setTotalMatched(0);
      setTruncated(false);
      setError('');
      setActiveStep(PASO_VISTA_PREVIA);
      return;
    }

    setError('');
    setLoading(true);
    try {
      const params = {
        fechaDesde: filtrosOrigen.fechaDesde,
        fechaHasta: filtrosOrigen.fechaHasta,
        nroTipoFormulario: filtrosOrigen.tipoFormulario.trim() || undefined,
        nroSuc: branch,
        maxRows: Math.min(maxRows, 15000),
      };
      const response = modulo === 'Contabilidad'
        ? await contabilidadInformesService.getRptContabilidad({
          ...params,
          nroCliente: entityId,
          nroCuenta: cuentasConsulta,
        })
        : modulo === 'Tesoreria'
          ? await tesoreriaInformesService.getRptCarterasDet({
            ...params,
            nroBanco,
          })
          : modulo === 'Ventas'
          ? await ventasInformesService.getRptVentas01({
            ...params,
            codigoArticulo: filtrosOrigen.codigoArticulo.trim() || undefined,
            nroCliente: entityId,
          })
          : await comprasInformesService.getRptCompras01({
            ...params,
            codigoArticulo: filtrosOrigen.codigoArticulo.trim() || undefined,
            nroProveedor: entityId,
          });

      setRawRows(response.data);
      setTotalMatched(response.totalMatched);
      setTruncated(response.truncated);
      setActiveStep(PASO_VISTA_PREVIA);
    } catch (requestError) {
      const message = getApiErrorMessage(requestError, 'No se pudo generar la vista previa del informe.');
      setError(message);
      enqueueSnackbar(message, { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const vistaPreviaPendiente = useRef(Boolean(definicion?.id));
  useEffect(() => {
    if (!vistaPreviaPendiente.current) return;
    vistaPreviaPendiente.current = false;
    void loadPreview();
  });

  const guardarDefinicion = async () => {
    if (!canSave) return;
    if (!titulo.trim()) {
      setError('Completá el título del informe.');
      setActiveStep(0);
      return;
    }
    if (!filtrosOrigen.fechaDesde || !filtrosOrigen.fechaHasta) {
      setError('Completá las fechas desde y hasta.');
      setActiveStep(2);
      return;
    }
    const borrador: BorradorInformePersonalizado = {
      titulo,
      modulo,
      tipo: tipoInforme,
      fechaDesde: filtrosOrigen.fechaDesde,
      fechaHasta: filtrosOrigen.fechaHasta,
      entidadId: filtrosOrigen.entidadId,
      codigoArticulo: filtrosOrigen.codigoArticulo,
      nroTipoFormulario: filtrosOrigen.tipoFormulario,
      nroSuc: filtrosOrigen.sucursal,
      nroCuenta: filtrosOrigen.nroCuenta,
      nroBanco: filtrosOrigen.nroBanco,
      columnas: ordenColumnas.filter((fieldId) => columnasSeleccionadas.includes(fieldId)),
      filtros: filtrosAdicionales.map((filtro) => ({
        fieldId: filtro.fieldId,
        operator: filtro.operator,
        value: filtro.value,
        valorHasta: filtro.valorHasta ?? '',
      })),
      campoAgrupacion,
      medida: campoValor,
      agregacion,
      tipoGrafico,
      usuariosVer,
      usuariosEditar,
      incluirUsuarios: canShare,
    };
    setGuardando(true);
    setErroresValidacion([]);
    setError('');
    try {
      const payload = armarPayloadInforme(
        borrador,
        fields.filter((field) => field.type === 'date').map((field) => field.id),
      );
      const guardado = savedId
        ? await informesPersonalizadosService.update(savedId, payload)
        : await informesPersonalizadosService.create(payload);
      setSavedId(guardado.id);
      setAccesoActual((actual) => (actual === 'propietario' ? actual : guardado.acceso));
      enqueueSnackbar('Informe guardado.', { variant: 'success' });
      onSaved?.(guardado);
    } catch (requestError) {
      if (statusHttp(requestError) === 403) {
        const message = getApiErrorMessage(requestError, 'No tenés permiso para guardar este informe.');
        setError(message);
        enqueueSnackbar(message, { variant: 'error' });
        return;
      }
      const validacion = leerErroresValidacion(requestError);
      if (validacion.length > 0) {
        setErroresValidacion(validacion);
        setError(getApiErrorMessage(requestError, 'Revisá los datos del informe.'));
        return;
      }
      const message = getApiErrorMessage(requestError, 'No se pudo guardar el informe.');
      setError(message);
      enqueueSnackbar(message, { variant: 'error' });
    } finally {
      setGuardando(false);
    }
  };

  const getExportRows = (): object[] => {
    if (isSummary && groupField && metricField) {
      return summaryRows.map((row) => ({
        [groupField.label]: row.groupValue,
        [summaryValueLabel]: row.value,
      }));
    }
    return filteredRows.map((row) => selectedFields.reduce<Record<string, unknown>>((result, field) => {
      result[field.label] = field.getValue(row);
      return result;
    }, {}));
  };

  const exportPreview = () => {
    const rows = getExportRows();
    if (rows.length === 0) {
      enqueueSnackbar('No hay datos para exportar.', { variant: 'info' });
      return;
    }
    const worksheet = XLSX.utils.json_to_sheet(rows);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, modulo);
    XLSX.writeFile(workbook, `${safeFileName(titulo || `informe-${modulo}`)}.xlsx`);
  };

  const renderConfiguration = () => (
    <Stack spacing={1.5}>
      <Accordion
        defaultExpanded
        disableGutters
        variant="outlined"
        sx={{ borderRadius: 2, '&:before': { display: 'none' }, boxShadow: 'none' }}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          sx={{ bgcolor: '#fafafa', minHeight: 48, '&.Mui-expanded': { minHeight: 48 } }}
        >
          <Typography fontWeight={600}>Período y filtros del origen</Typography>
        </AccordionSummary>
        <AccordionDetails sx={{ pt: 0, pb: 2 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {modulo === 'Contabilidad'
              ? 'El período, la cuenta, el cliente, el tipo y la sucursal se envían a la consulta. El filtro adicional Número de cuenta también, antes del máximo de filas.'
              : modulo === 'Tesoreria'
                ? 'Estos criterios se aplican al resultado de BP_RPT_CarterasDet_vw.'
                : `Estos criterios se envían al informe de ${nombreModulo(modulo)}; los filtros adicionales se aplican a las filas cargadas.`}
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                size="small"
                label="Fecha desde"
                type="date"
                value={filtrosOrigen.fechaDesde}
                onChange={(event) => setOriginField('fechaDesde', event.target.value)}
                InputLabelProps={{ shrink: true }}
                required
                error={Boolean(errorDe('filtrosOrigen.fechaDesde'))}
                helperText={errorDe('filtrosOrigen.fechaDesde')}
                sx={filtroFieldSx}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                size="small"
                label="Fecha hasta"
                type="date"
                value={filtrosOrigen.fechaHasta}
                onChange={(event) => setOriginField('fechaHasta', event.target.value)}
                InputLabelProps={{ shrink: true }}
                required
                error={Boolean(errorDe('filtrosOrigen.fechaHasta'))}
                helperText={errorDe('filtrosOrigen.fechaHasta')}
                sx={filtroFieldSx}
              />
            </Grid>
            {modulo === 'Tesoreria' ? (
              <Grid item xs={12} sm={6} md={3}>
                <SearchableSelect
                  label="Cartera o cuenta"
                  allowEmpty
                  emptyOptionLabel="Todas"
                  loading={loadingBancos}
                  size="small"
                  value={filtrosOrigen.nroBanco.trim() ? Number(filtrosOrigen.nroBanco) : null}
                  onChange={(value) => setOriginField('nroBanco', value == null ? '' : String(value))}
                  options={bancos.map((banco) => ({
                    value: banco.nroBanco,
                    label: banco.nombreBanco?.trim()
                      ? `${banco.nroBanco} — ${banco.nombreBanco.trim()}`
                      : String(banco.nroBanco),
                  }))}
                  helperText={errorDe('filtrosOrigen.nroBanco') || 'Vacío = todas'}
                  error={Boolean(errorDe('filtrosOrigen.nroBanco'))}
                  sx={filtroFieldSx}
                />
              </Grid>
            ) : (
            <Grid item xs={12} sm={6} md={3}>
              <Stack direction="row" spacing={1} alignItems="flex-start">
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  {modulo === 'Compras' ? (
                    <ProveedorInlineSearchField
                      label="Proveedor"
                      placeholder="Número, nombre o CUIT — vacío = todos"
                      size="small"
                      displayValue={entidadDisplay}
                      onSelect={(proveedor) => {
                        setOriginField('entidadId', String(proveedor.nroProveedor));
                        setEntidadNombre(proveedor.nombre ?? '');
                      }}
                      onOpenModal={() => setEntidadSearchOpen(true)}
                    />
                  ) : (
                    <ClienteInlineSearchField
                      label="Cliente"
                      placeholder="Número, nombre o CUIT — vacío = todos"
                      size="small"
                      excluirInactivos={false}
                      displayValue={entidadDisplay}
                      onSelect={(cliente) => {
                        setOriginField('entidadId', String(cliente.nroCliente));
                        setEntidadNombre(cliente.nombre ?? '');
                      }}
                      onOpenModal={() => setEntidadSearchOpen(true)}
                    />
                  )}
                </Box>
                {filtrosOrigen.entidadId.trim() && (
                  <Button
                    size="small"
                    onClick={() => {
                      setOriginField('entidadId', '');
                      setEntidadNombre('');
                    }}
                    sx={{ mt: 0.5, textTransform: 'none', flexShrink: 0 }}
                  >
                    Limpiar
                  </Button>
                )}
              </Stack>
            </Grid>
            )}
            {modulo === 'Contabilidad' ? (
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  fullWidth
                  size="small"
                  label="Cuenta"
                  value={filtrosOrigen.nroCuenta}
                  onChange={(event) => setOriginField('nroCuenta', event.target.value.replace(/\D/g, ''))}
                  placeholder="Vacío = todas"
                  helperText={errorDe('filtrosOrigen.nroCuenta') || 'Número de cuenta de la línea. Vacío = todas'}
                  error={Boolean(errorDe('filtrosOrigen.nroCuenta'))}
                  sx={filtroFieldSx}
                />
              </Grid>
            ) : modulo === 'Tesoreria' ? null : (
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                size="small"
                label="Código de artículo"
                value={filtrosOrigen.codigoArticulo}
                onChange={(event) => {
                  setOriginField('codigoArticulo', event.target.value);
                  setArticuloNombre('');
                }}
                helperText={articuloNombre || undefined}
                sx={filtroFieldSx}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        edge="end"
                        size="small"
                        color="primary"
                        title="Buscar artículo"
                        onClick={() => setProductoSearchOpen(true)}
                      >
                        <ArrowDropDownIcon />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            )}
            <Grid item xs={12} sm={6} md={3}>
              <SearchableSelect
                label="Tipo de comprobante"
                allowEmpty
                emptyOptionLabel="Todos"
                loading={loadingTipos}
                size="small"
                value={filtrosOrigen.tipoFormulario || null}
                onChange={(value) => setOriginField('tipoFormulario', value == null ? '' : String(value))}
                options={tiposFormulario.map((tipo) => ({
                  value: tipo.nroTipoFormulario,
                  label: tipo.descripcion?.trim()
                    ? `${tipo.nroTipoFormulario} — ${tipo.descripcion.trim()}`
                    : tipo.nroTipoFormulario,
                }))}
                sx={filtroFieldSx}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <SearchableSelect
                label="Sucursal"
                allowEmpty
                emptyOptionLabel="Todas"
                loading={loadingSucursales}
                size="small"
                value={filtrosOrigen.sucursal.trim() ? Number(filtrosOrigen.sucursal) : null}
                onChange={(value) => setOriginField('sucursal', value == null ? '' : String(value))}
                options={sucursales.map((sucursal) => ({
                  value: sucursal.nroSuc,
                  label: sucursal.nombreSucursal?.trim()
                    ? `${String(sucursal.nroSuc).padStart(5, '0')} — ${sucursal.nombreSucursal.trim()}`
                    : String(sucursal.nroSuc).padStart(5, '0'),
                }))}
                sx={filtroFieldSx}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                size="small"
                label="Máximo de filas"
                type="number"
                value={filtrosOrigen.maxRows}
                onChange={(event) => setOriginField('maxRows', event.target.value)}
                inputProps={{ min: 1, max: 15000 }}
                helperText="Máximo permitido: 15.000"
                sx={filtroFieldSx}
              />
            </Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>

        <Accordion
          defaultExpanded
          disableGutters
          variant="outlined"
          sx={{ borderRadius: 2, '&:before': { display: 'none' }, boxShadow: 'none' }}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            sx={{ bgcolor: '#fafafa', minHeight: 48, '&.Mui-expanded': { minHeight: 48 } }}
          >
            <Typography fontWeight={600}>
              Columnas ({selectedColumnFields.length} seleccionadas)
            </Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ pt: 0, pb: 2 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Elegí las columnas disponibles y definí el orden en que aparecerán en el informe.
            </Typography>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', md: 'minmax(0, 1fr) auto minmax(0, 1fr)' },
                gap: 2,
                alignItems: 'center',
              }}
            >
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>Columnas disponibles</Typography>
                <List
                  aria-label="Columnas disponibles"
                  aria-multiselectable="true"
                  role="listbox"
                  sx={{
                    border: 1,
                    borderColor: 'divider',
                    borderRadius: 1,
                    bgcolor: 'background.paper',
                    minHeight: 224,
                    maxHeight: 280,
                    overflow: 'auto',
                    py: 0.5,
                  }}
                >
                  {availableFields.length === 0 ? (
                    <ListItem><ListItemText secondary="No hay más columnas disponibles." /></ListItem>
                  ) : availableFields.map((field) => (
                    <ListItemButton
                      key={field.id}
                      dense
                      role="option"
                      aria-selected={columnasDisponiblesMarcadas.includes(field.id)}
                      selected={columnasDisponiblesMarcadas.includes(field.id)}
                      onClick={() => toggleMarkedColumn(
                        field.id,
                        columnasDisponiblesMarcadas,
                        setColumnasDisponiblesMarcadas,
                      )}
                    >
                      <ListItemText primary={field.label} />
                    </ListItemButton>
                  ))}
                </List>
              </Box>

              <Stack
                direction={{ xs: 'row', md: 'column' }}
                spacing={1}
                justifyContent="center"
                alignItems="center"
              >
                <Button
                  aria-label="Agregar columnas seleccionadas"
                  variant="outlined"
                  size="small"
                  sx={accionButtonSx}
                  endIcon={<ArrowForwardIcon />}
                  disabled={columnasDisponiblesMarcadas.length === 0}
                  onClick={addSelectedColumns}
                >
                  Agregar
                </Button>
                <Button
                  aria-label="Quitar columnas seleccionadas"
                  variant="outlined"
                  size="small"
                  sx={accionButtonSx}
                  startIcon={<ArrowBackIcon />}
                  disabled={columnasSeleccionadasMarcadas.length === 0}
                  onClick={removeSelectedColumns}
                >
                  Quitar
                </Button>
              </Stack>

              <Box sx={{ minWidth: 0 }}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>Columnas seleccionadas</Typography>
                <List
                  aria-label="Columnas seleccionadas"
                  aria-multiselectable="true"
                  role="listbox"
                  sx={{
                    border: 1,
                    borderColor: 'divider',
                    borderRadius: 1,
                    bgcolor: 'background.paper',
                    minHeight: 224,
                    maxHeight: 280,
                    overflow: 'auto',
                    py: 0.5,
                  }}
                >
                  {selectedColumnFields.length === 0 ? (
                    <ListItem><ListItemText secondary="Seleccioná al menos una columna." /></ListItem>
                  ) : selectedColumnFields.map((field, index) => (
                    <ListItem
                      key={field.id}
                      disablePadding
                      secondaryAction={(
                        <Stack direction="row">
                          <IconButton
                            aria-label={`Mover ${field.label} hacia arriba`}
                            size="small"
                            disabled={index === 0}
                            onClick={() => moveSelectedColumn(field.id, -1)}
                          >
                            <KeyboardArrowUpIcon />
                          </IconButton>
                          <IconButton
                            aria-label={`Mover ${field.label} hacia abajo`}
                            size="small"
                            disabled={index === selectedColumnFields.length - 1}
                            onClick={() => moveSelectedColumn(field.id, 1)}
                          >
                            <KeyboardArrowDownIcon />
                          </IconButton>
                        </Stack>
                      )}
                    >
                      <ListItemButton
                        dense
                        role="option"
                        aria-selected={columnasSeleccionadasMarcadas.includes(field.id)}
                        selected={columnasSeleccionadasMarcadas.includes(field.id)}
                        onClick={() => toggleMarkedColumn(
                          field.id,
                          columnasSeleccionadasMarcadas,
                          setColumnasSeleccionadasMarcadas,
                        )}
                      >
                        <ListItemText primary={field.label} />
                      </ListItemButton>
                    </ListItem>
                  ))}
                </List>
              </Box>
            </Box>
            {!esModuloVista(modulo) && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5 }}>
              “Cantidad de artículos (unidades)” es la cantidad registrada en cada renglón del comprobante. Al resumirla con Suma, obtendrás el total de unidades vendidas o compradas.
            </Typography>
            )}
          </AccordionDetails>
        </Accordion>

      <Accordion
        disableGutters
        variant="outlined"
        sx={{ borderRadius: 2, '&:before': { display: 'none' }, boxShadow: 'none' }}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          sx={{ bgcolor: '#fafafa', minHeight: 48, '&.Mui-expanded': { minHeight: 48 } }}
        >
          <Typography fontWeight={600}>Filtros adicionales ({filtrosAdicionales.length})</Typography>
        </AccordionSummary>
        <AccordionDetails sx={{ pt: 0, pb: 2 }}>
          {modulo === 'Contabilidad' && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Número de cuenta se consulta en el origen, antes del máximo de filas. El resto de los filtros se aplica sobre las filas ya cargadas.
            </Typography>
          )}
          <FiltrosAdicionalesVentas
            modulo={modulo}
            filtros={filtrosAdicionales}
            campos={fields}
            sucursales={sucursales}
            loadingSucursales={loadingSucursales}
            tiposFormulario={tiposFormulario}
            loadingTipos={loadingTipos}
            usuariosEmpresa={usuariosEmpresa}
            onChange={setFiltrosAdicionales}
            nuevoId={() => filterIdRef.current++}
            errorDe={errorDe}
          />
        </AccordionDetails>
      </Accordion>

      {isSummary && (
        <Accordion
          defaultExpanded
          disableGutters
          variant="outlined"
          sx={{ borderRadius: 2, '&:before': { display: 'none' }, boxShadow: 'none' }}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            sx={{ bgcolor: '#fafafa', minHeight: 48, '&.Mui-expanded': { minHeight: 48 } }}
          >
            <Typography fontWeight={600}>Agrupamiento y resumen</Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ pt: 0, pb: 2 }}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <FormControl fullWidth>
                <InputLabel>Agrupar por</InputLabel>
                <Select
                  label="Agrupar por"
                  value={campoAgrupacion}
                  onChange={(event) => setCampoAgrupacion(event.target.value)}
                >
                  {fields.map((field) => (
                    <MenuItem key={field.id} value={field.id}>{field.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl fullWidth>
                <InputLabel>Operación</InputLabel>
                <Select
                  label="Operación"
                  value={agregacion}
                  onChange={(event) => setAgregacion(event.target.value as InformeAggregation)}
                >
                  {AGGREGATIONS.map((item) => (
                    <MenuItem key={item.value} value={item.value}>{item.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              {agregacion !== 'count' && (
                <FormControl fullWidth>
                  <InputLabel>Medida a resumir</InputLabel>
                  <Select
                    label="Medida a resumir"
                    value={campoValor}
                    onChange={(event) => setCampoValor(event.target.value)}
                  >
                    {numericFields.map((field) => (
                      <MenuItem key={field.id} value={field.id}>{field.label}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
              {isSummary && renderTipoGrafico()}
            </Stack>
          </AccordionDetails>
        </Accordion>
      )}
    </Stack>
  );

  const renderPreviewTable = () => {
    const previewRows = isSummary ? summaryRows : filteredRows;
    const displayFields = isSummary ? [] : selectedFields;

    if (previewRows.length === 0) {
      return (
        <Alert severity="info">
          No hay filas que coincidan con los criterios. Probá otro período o revisá los filtros.
        </Alert>
      );
    }

    return (
      <TableContainer sx={{ maxHeight: 520, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
        <Table stickyHeader size="small">
          <TableHead>
            <TableRow>
              {isSummary ? (
                <>
                  <TableCell>{groupField?.label ?? 'Grupo'}</TableCell>
                  <TableCell align="right">{summaryValueLabel}</TableCell>
                </>
              ) : displayFields.map((field) => (
                <TableCell key={field.id}>{field.label}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {isSummary
              ? summaryRows.slice(0, 500).map((row) => (
                <TableRow key={row.groupValue} hover>
                  <TableCell>{row.groupValue}</TableCell>
                  <TableCell align="right">{formatValue(row.value)}</TableCell>
                </TableRow>
              ))
              : filteredRows.slice(0, 500).map((row, index) => (
                <TableRow key={index} hover>
                  {displayFields.map((field) => (
                    <TableCell key={field.id}>{formatValue(field.getValue(row))}</TableCell>
                  ))}
                </TableRow>
              ))}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  const renderChart = () => {
    if (tipoGrafico === 'ninguno' || summaryRows.length === 0) return null;
    const chartRows = [...summaryRows]
      .sort((left, right) => Math.abs(right.value) - Math.abs(left.value))
      .slice(0, 20);
    const chartProps = { data: chartRows, margin: { top: 16, right: 20, left: 8, bottom: 50 } };
    if (tipoGrafico === 'torta') {
      return (
        <ResponsiveContainer width="100%" height={340}>
          <PieChart>
            <Tooltip />
            <Pie data={chartRows} dataKey="value" nameKey="groupValue" outerRadius={115} label>
              {chartRows.map((row, index) => (
                <Cell key={row.groupValue} fill={CHART_COLORS[index % CHART_COLORS.length]} />
              ))}
            </Pie>
          </PieChart>
        </ResponsiveContainer>
      );
    }
    if (tipoGrafico === 'area') {
      return (
        <ResponsiveContainer width="100%" height={340}>
          <AreaChart {...chartProps}>
            <XAxis dataKey="groupValue" angle={-25} textAnchor="end" interval="preserveStartEnd" />
            <YAxis />
            <Tooltip />
            <Area type="monotone" dataKey="value" name={summaryValueLabel} stroke="#1976d2" fill="#90caf9" />
          </AreaChart>
        </ResponsiveContainer>
      );
    }
    const Chart = tipoGrafico === 'lineas' ? LineChart : BarChart;
    return (
      <ResponsiveContainer width="100%" height={340}>
        <Chart {...chartProps}>
          <XAxis dataKey="groupValue" angle={-25} textAnchor="end" interval="preserveStartEnd" />
          <YAxis />
          <Tooltip />
          {tipoGrafico === 'lineas'
            ? <Line type="monotone" dataKey="value" name={summaryValueLabel} stroke="#1976d2" />
            : <Bar dataKey="value" name={summaryValueLabel} fill="#1976d2" />}
        </Chart>
      </ResponsiveContainer>
    );
  };

  const renderTipoGrafico = () => (
    <FormControl fullWidth size="small" error={Boolean(errorDe('resumen.tipoGrafico'))} sx={filtroFieldSx}>
      <InputLabel>Tipo de gráfico</InputLabel>
      <Select
        label="Tipo de gráfico"
        value={tipoGrafico}
        onChange={(event) => setTipoGrafico(event.target.value as TipoGrafico)}
      >
        <MenuItem value="ninguno">Sin gráfico</MenuItem>
        <MenuItem value="barras">Barras</MenuItem>
        <MenuItem value="lineas">Líneas</MenuItem>
        <MenuItem value="torta">Torta</MenuItem>
        <MenuItem value="area">Área</MenuItem>
      </Select>
    </FormControl>
  );

  const stepContent = () => {
    if (activeStep === 0) {
      return (
        <Stack spacing={3}>
          <TextField
            label="Título del informe"
            value={titulo}
            onChange={(event) => setTitulo(event.target.value)}
            fullWidth
            error={Boolean(errorDe('titulo'))}
            helperText={errorDe('titulo') || 'Identifica el informe en el listado.'}
          />
          <FormControl error={Boolean(errorDe('tipo'))}>
            <Typography variant="h6" sx={{ mb: 1 }}>Elegí el tipo de informe</Typography>
            <RadioGroup value={tipoInforme} onChange={(event) => setTipoInforme(event.target.value as TipoInforme)}>
              <FormControlLabel
                value="detalle"
                control={<Radio />}
                label={<Box><Typography fontWeight={600}>Detalle</Typography><Typography variant="body2" color="text.secondary">Elegí las columnas que querés consultar.</Typography></Box>}
              />
              <FormControlLabel
                value="resumen"
                control={<Radio />}
                label={<Box><Typography fontWeight={600}>Resumen</Typography><Typography variant="body2" color="text.secondary">Agrupá registros y, si querés, mostrá un gráfico.</Typography></Box>}
              />
            </RadioGroup>
            {errorDe('tipo') && <Typography variant="caption" color="error">{errorDe('tipo')}</Typography>}
          </FormControl>
          {tipoInforme === 'resumen' && (
            <Box sx={{ maxWidth: 420 }}>
              {renderTipoGrafico()}
            </Box>
          )}
        </Stack>
      );
    }
    if (activeStep === 1) {
      return (
        <Stack spacing={2}>
          <Typography variant="h6">Origen disponible</Typography>
          <Card variant="outlined" sx={{ borderColor: 'primary.main', bgcolor: 'action.hover' }}>
            <CardContent>
              <Stack direction="row" spacing={2} alignItems="center">
                <BarChartIcon color="primary" />
                <Box>
                  <Typography fontWeight={600}>{tituloOrigen(modulo)}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {detalleOrigen(modulo)}
                  </Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>
          <Alert severity="info">
            {modulo === 'Contabilidad'
              ? 'La vista previa consulta la vista BP_RPT_Contabilidad_vw.'
              : modulo === 'Tesoreria'
                ? 'La vista previa consulta BP_RPT_CarterasDet_vw.'
                : `La vista previa consulta los comprobantes y artículos de ${nombreModulo(modulo)}.`}
          </Alert>
        </Stack>
      );
    }
    if (activeStep === 2) return renderConfiguration();
    if (activeStep === PASO_COMPARTIR) {
      return (
        <Stack spacing={2}>
          <Box>
            <Typography variant="h6">Compartir</Typography>
            <Typography variant="body2" color="text.secondary">
              {canShare
                ? 'Elegí quién de la empresa puede ver o modificar este informe.'
                : 'Solo quien creó el informe puede cambiar con quién se comparte.'}
            </Typography>
          </Box>
          {usuariosEmpresaError && <Alert severity="warning">{usuariosEmpresaError}</Alert>}
          <Autocomplete
            multiple
            disabled={!canShare}
            options={opcionesUsuarios}
            value={opcionesUsuarios.filter((usuario) => usuariosVer.includes(usuario.id))}
            getOptionLabel={(option) => option.usuario ? `${option.nombre} (${option.usuario})` : option.nombre}
            isOptionEqualToValue={(option, selected) => option.id === selected.id}
            onChange={(_, value) => setUsuariosVer(value.map((usuario) => usuario.id))}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Pueden consultar"
                error={Boolean(errorDe('usuariosVer'))}
                helperText={errorDe('usuariosVer') || 'Ven el informe y no pueden modificarlo.'}
              />
            )}
          />
          <Autocomplete
            multiple
            disabled={!canShare}
            options={opcionesUsuarios}
            value={opcionesUsuarios.filter((usuario) => usuariosEditar.includes(usuario.id))}
            getOptionLabel={(option) => option.usuario ? `${option.nombre} (${option.usuario})` : option.nombre}
            isOptionEqualToValue={(option, selected) => option.id === selected.id}
            onChange={(_, value) => setUsuariosEditar(value.map((usuario) => usuario.id))}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Pueden editar"
                error={Boolean(errorDe('usuariosEditar'))}
                helperText={errorDe('usuariosEditar') || 'Ven y editan la definición. Quien edita ya puede consultar.'}
              />
            )}
          />
        </Stack>
      );
    }

    return (
      <Stack spacing={2.5}>
        <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={1}>
          <Box>
            <Typography variant="h6">{titulo.trim() || `Informe de ${modulo.toLowerCase()}`}</Typography>
            <Typography variant="body2" color="text.secondary">
              {filteredRows.length} filas en la vista previa de {totalMatched} coincidentes en el origen.
            </Typography>
          </Box>
          <Button variant="outlined" startIcon={<DownloadIcon />} onClick={exportPreview} sx={accionButtonSx}>
            Descargar Excel
          </Button>
        </Stack>
        {truncated && (
          <Alert severity="warning">
            El origen limitó la respuesta a {rawRows.length} filas de {totalMatched}. Ajustá los filtros o el máximo para consultar otro subconjunto.
          </Alert>
        )}
        {isSummary && tipoGrafico !== 'ninguno' && renderChart()}
        {isSummary && summaryRows.length > 500 && (
          <Typography variant="body2" color="text.secondary">
            La tabla muestra los primeros 500 grupos. La descarga incluye todos los grupos.
          </Typography>
        )}
        {isSummary && tipoGrafico !== 'ninguno' && summaryRows.length > 20 && (
          <Alert severity="info">
            El gráfico muestra los 20 grupos con mayor valor; la tabla y la descarga incluyen todos los grupos.
          </Alert>
        )}
        {renderPreviewTable()}
        {!isSummary && filteredRows.length > 500 && (
          <Typography variant="body2" color="text.secondary">
            La tabla muestra las primeras 500 filas. La descarga incluye todas las filas cargadas.
          </Typography>
        )}
      </Stack>
    );
  };

  return (
    <>
        <Stack spacing={3}>
          <Stack direction={{ xs: 'column', sm: 'row' }} alignItems={{ xs: 'stretch', sm: 'center' }} justifyContent="space-between" spacing={1.5}>
            <Box>
              <Typography variant="h4" component="h1" sx={{ fontWeight: 600, color: '#1976d2' }}>
                {readOnly ? 'Consulta de informe' : savedId ? 'Editar informe' : `Nuevo informe de ${nombreModulo(modulo)}`}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {readOnly
                  ? 'Podés revisar la definición y la vista previa.'
                  : 'Guardá la definición para volver a abrirla. La vista previa y el Excel usan el informe que ya existe.'}
              </Typography>
            </Box>
            <Button startIcon={<ArrowBackIcon />} onClick={onBack} sx={{ alignSelf: { xs: 'flex-start', sm: 'center' }, textTransform: 'none' }}>
              Volver al listado
            </Button>
          </Stack>

          <Alert severity="info">
            {readOnly
              ? 'Estás consultando este informe. No podés guardarlo, compartirlo ni eliminarlo.'
              : modulo === 'Contabilidad'
                ? 'La vista previa consulta los movimientos de BP_RPT_Contabilidad_vw. Guardar solo persiste esta definición.'
                : modulo === 'Tesoreria'
                  ? 'La vista previa consulta los movimientos de BP_RPT_CarterasDet_vw. Guardar solo persiste esta definición.'
                  : `La vista previa consulta el informe de ${nombreModulo(modulo)} actual. Guardar solo persiste esta definición.`}
          </Alert>

          <Stepper activeStep={activeStep} alternativeLabel>
            {STEPS.map((label) => (
              <Step key={label}><StepLabel>{label}</StepLabel></Step>
            ))}
          </Stepper>

          {error && <Alert severity="error" onClose={() => setError('')}>{error}</Alert>}
          {erroresValidacion.length > 0 && (
            <Alert severity="error">
              <Stack spacing={0.5}>
                {erroresValidacion.map((item) => (
                  <Typography key={`${item.clave}-${item.mensaje}`} variant="body2">
                    {item.clave}: {item.mensaje}
                  </Typography>
                ))}
              </Stack>
            </Alert>
          )}

          <Box
            component={readOnly && activeStep < PASO_VISTA_PREVIA ? 'fieldset' : 'div'}
            disabled={readOnly && activeStep < PASO_VISTA_PREVIA}
            sx={{ border: 0, p: 0, m: 0, minWidth: 0, minHeight: 260 }}
          >
            {stepContent()}
          </Box>

          <Stack direction="row" justifyContent="space-between" spacing={1}>
            <Button
              startIcon={<ArrowBackIcon />}
              disabled={activeStep === 0 || loading || guardando}
              onClick={() => {
                setError('');
                setActiveStep((step) => Math.max(0, step - 1));
              }}
              sx={{ textTransform: 'none' }}
            >
              Anterior
            </Button>
            <Stack direction="row" spacing={1}>
              {activeStep < PASO_COMPARTIR ? (
                <Button
                  variant="contained"
                  endIcon={<ArrowForwardIcon />}
                  disabled={guardando}
                  onClick={() => {
                    setError('');
                    setActiveStep((step) => step + 1);
                  }}
                  sx={accionButtonSx}
                >
                  Continuar
                </Button>
              ) : activeStep === PASO_COMPARTIR ? (
                <Button
                  variant="contained"
                  startIcon={loading ? <CircularProgress size={18} color="inherit" /> : <PlayArrowIcon />}
                  disabled={loading || guardando}
                  onClick={() => void loadPreview()}
                  sx={accionButtonSx}
                >
                  {loading ? 'Generando vista previa…' : 'Generar vista previa'}
                </Button>
              ) : canSave ? (
                <Button
                  variant="contained"
                  disabled={guardando || loading}
                  onClick={() => void guardarDefinicion()}
                  sx={accionButtonSx}
                >
                  {guardando ? 'Guardando…' : 'Guardar'}
                </Button>
              ) : null}
            </Stack>
          </Stack>
        </Stack>
      {modulo === 'Compras' ? (
        <ProveedorSearch
          open={entidadSearchOpen}
          onClose={() => setEntidadSearchOpen(false)}
          onSelect={(proveedor) => {
            setOriginField('entidadId', String(proveedor.nroProveedor));
            setEntidadNombre(proveedor.nombre ?? '');
            setEntidadSearchOpen(false);
          }}
        />
      ) : (
        <ClienteSearch
          open={entidadSearchOpen}
          onClose={() => setEntidadSearchOpen(false)}
          excluirInactivos={false}
          onSelect={(cliente) => {
            setOriginField('entidadId', String(cliente.nroCliente));
            setEntidadNombre(cliente.nombre ?? '');
            setEntidadSearchOpen(false);
          }}
        />
      )}
      <ProductoSearch
        open={productoSearchOpen}
        onClose={() => setProductoSearchOpen(false)}
        onSelect={(producto) => {
          setOriginField('codigoArticulo', producto.codigoArticulo ?? '');
          setArticuloNombre(producto.descripcion ?? '');
          setProductoSearchOpen(false);
        }}
      />
    </>
  );
};

export default InformeDinamicoWizard;
